import {
    CreateExtArgs,
    CreateExtensionArgument,
    createSnapshotChain,
    createStore,
    DalValue,
    debug,
    DmStore,
    Extension,
    ExtensionAPI,
    InitializeExtArgs,
    isConflicting as isConflictingValue,
    LoggerDriver,
    pointerUtils,
    SnapshotDal,
    createFragmentStore,
    FragmentStore,
    Store
} from '@wix/document-manager-core'
import type {SaveStateApi} from './saveState'
import {Stack} from '@wix/document-manager-utils'
import type {Pointer, ResolvedReference, SetOperationsQueue, UndoRedoConfig} from '@wix/document-services-types'
import _ from 'lodash'
import {NON_UNDOABLE_KEYS, UNDOABLE_TYPES} from '../constants/constants'
import type {RelationshipsAPI} from './relationships'
import type {SnapshotExtApi} from './snapshots'

const {getPointer} = pointerUtils

const UNDO_REDO_TAG = 'undoRedo'
const INITIAL_HISTORY_LABEL = 'INITIAL_HISTORY_SNAPSHOT_LABEL'

const EVENTS = {
    UNDO_REDO: {
        TAKE_SNAPSHOT: 'TAKE_SNAPSHOT',
        UNDO: 'UNDO',
        REDO: 'REDO'
    }
}

interface SnapshotRef {
    id: number
    label: string
    index?: number
    tag: string
    params?: any
    placeholder?: any
}

export const undoRedoConfig: UndoRedoConfig = {
    undoableTypes: UNDOABLE_TYPES,
    nonUndoableKeys: NON_UNDOABLE_KEYS,
    nogoNamespaces: ['DESKTOP', 'MOBILE', 'variants']
}

const createExtension = ({dsConfig, environmentContext}: CreateExtensionArgument): Extension => {
    const concurrentExperimentOpen = dsConfig.cedit
    const undoStack = new Stack<SnapshotRef>('Undo')
    const redoStack = new Stack<SnapshotRef>('Redo')
    let lastSnapshotId: number = 0
    let currentSnapshotRef: SnapshotRef = null as unknown as SnapshotRef
    let soq: SetOperationsQueue | null = null

    const createSnapshotRef = (label: string, index: number | undefined, additionalProps: any = {}): SnapshotRef => ({
        id: ++lastSnapshotId,
        tag: UNDO_REDO_TAG,
        label,
        index,
        ...additionalProps
    })

    const takeInitialSnapshotForUndoRedo = (snapshotExtAPI: SnapshotExtApi) => {
        const {snapshots} = snapshotExtAPI
        currentSnapshotRef = createSnapshotRef(INITIAL_HISTORY_LABEL, snapshots.takeSnapshot(UNDO_REDO_TAG))
    }

    const createExtensionAPI = ({extensionAPI, dal, eventEmitter, coreConfig}: CreateExtArgs): UndoRedoExtApi => {
        const {snapshots} = extensionAPI as SnapshotExtApi
        const {relationships} = extensionAPI as RelationshipsAPI
        const {logger} = coreConfig
        const log: LoggerDriver = debug('undo', environmentContext.loggerDriver)
        const {undoableTypes, nonUndoableKeys, nogoNamespaces} = coreConfig.undoRedoConfig ?? undoRedoConfig
        const shouldUseFragments: boolean = coreConfig.experimentInstance.isOpen('dm_fragments')

        /**
         * get the snapshot store in the correct type (FragmentStore/DmStore) according to the experiment
         * @param snapshot
         */
        const getSnapshotStore = (snapshot: SnapshotDal): FragmentStore | DmStore => {
            return shouldUseFragments ? snapshot.getAllFragmentsStore() : snapshot.getStore()
        }

        /**
         * merge storeToMerge into StoreToMergeTo. Used due to type differences determined by whether we use fragments or not.
         * @param storeToMergeTo
         * @param storeToMerge
         */
        const mergeStores = (storeToMergeTo: Store, storeToMerge: Store) => {
            if (shouldUseFragments) {
                ;(storeToMergeTo as FragmentStore).merge(storeToMerge as FragmentStore)
            } else {
                ;(storeToMergeTo as DmStore).merge(storeToMerge as DmStore)
            }
        }

        const applyRevertOperation = (store: Store, operation: (pointer: Pointer) => void): void => {
            if (shouldUseFragments) {
                ;(store as FragmentStore).forEach((fragmentStore, fragmentType) => {
                    fragmentStore.forEach((pointer: Pointer) => {
                        operation(getPointer(pointer.id, pointer.type, {fragmentType}))
                    })
                })
            } else {
                ;(store as DmStore).forEach((pointer: Pointer) => operation(pointer))
            }
        }

        /**
         * Reverts the keys in snapshot to the state at revertToSnapshot unless they're already contained in revertStore
         * @param snapshot - snapshot to be reverted
         * @param revertToSnapshot - state to revert back to
         * @param revertStore - store containing the reverted values
         */
        const revertSnapshot = (snapshot: SnapshotDal, revertToSnapshot: SnapshotDal, revertStore: Store) => {
            applyRevertOperation(getSnapshotStore(snapshot), (pointer: Pointer) => {
                if (!revertStore.has(pointer)) {
                    revertStore.set(pointer, revertToSnapshot.getValue(pointer))
                }
            })
        }

        const hasSignature = (pointer: Pointer) => dal.hasSignature(pointer)

        /**
         * Returns true if storeToCheck conflicts with baseSnapshot, else returns false
         * @param storeToCheck
         * @param baseSnapshot
         */
        const hasSignatureConflict = (storeToCheck: Store, baseSnapshot: SnapshotDal): boolean =>
            storeToCheck.some(
                (pointer, value) =>
                    hasSignature(pointer) &&
                    isConflictingValue(
                        value,
                        baseSnapshot.getValue(pointer),
                        storeToCheck.get(dal.getBasedOnSignaturePointer(pointer))
                    )
            )

        /**
         * Checks if any pointer in storeToCheck refers to a deleted pointer in revertStore
         * @param foreignStore
         * @param revertStore
         * @returns true if foreignStore refers to a deleted pointer in revertStore
         */
        const dependsOnDeletedPointer = (foreignStore: Store, revertStore: Store): boolean => {
            const deletedRevertPointers: Store = revertStore.filter(
                (pointer: Pointer, value: DalValue) => value === undefined && !foreignStore.has(pointer)
            )
            if (deletedRevertPointers.isEmpty()) {
                return false
            }
            return foreignStore.some((pointer: Pointer, value: any) => {
                if (value) {
                    const refs: readonly ResolvedReference[] = relationships.extractReferences(pointer.type, value)
                    return refs.some((ref: ResolvedReference) =>
                        deletedRevertPointers.has(getPointer(ref.id, ref.referencedMap))
                    )
                }
                return false
            })
        }

        /**
         * Returns true if revertStore refers to a deleted pointer in foreignStore
         * @param foreignStore
         * @param revertStore
         * @returns true if revertStore refers to a deleted pointer in foreignStore
         */
        const refersToDeletedPointer = (revertStore: Store, foreignStore: Store) =>
            foreignStore.some((pointer: Pointer, value: any) => {
                if (value === undefined) {
                    const refs: Pointer[] = relationships.getReferencesToPointer(pointer)
                    return refs.some(ptr => revertStore.has(ptr))
                }
                return false
            })

        /**
         * Returns true if storeToCheck has either a signature dependency or a reference dependency to a deleted pointer to baseSnapshot
         * @param foreignStore
         * @param revertSnapshot
         * @returns true if there is a signature conflict or dependency relationship, else returns false
         */
        const isConflictingOrDependent = (foreignStore: Store, revertCommit: SnapshotDal): boolean => {
            const revertStore = getSnapshotStore(revertCommit)
            return (
                hasSignatureConflict(foreignStore, revertCommit) ||
                dependsOnDeletedPointer(foreignStore, revertStore) ||
                refersToDeletedPointer(revertStore, foreignStore)
            )
        }

        /**
         * Returns true if the pointer value causes the container snapshot to be defined as a NOGO snapshot
         * @param pointer
         * @param value
         * @returns
         */
        const isNogoPointerValue = (pointer: Pointer, value: any) =>
            value === undefined && hasSignature(pointer) && nogoNamespaces.includes(pointer.type)

        /**
         * Returns true if the snapshot is a NOGO snapshot, else returns false
         * A NOGO snapshot is a foreign snapshot that should not be reverted.
         * Undoable foreign snapshots are not NOGO - they should be reverted.
         * @param snapshot
         */
        const isNogo = (snapshot: SnapshotDal): boolean => {
            if (snapshot.isUndoable) {
                return false
            }
            const snapshotStore = getSnapshotStore(snapshot)
            return snapshot.isForeign && snapshotStore.some(isNogoPointerValue)
        }

        /**
         * Returns a set containing all the snapshots (local and foreign) that are dependencies of NOGO snapshots
         * @param chain
         */
        const markNogosAndDependencies = (chain: SnapshotDal[]): Set<SnapshotDal> => {
            const aggStore: Store = shouldUseFragments ? createFragmentStore() : createStore()
            const nogosAndDependencies = new Set<SnapshotDal>()
            _.forEachRight(chain, (snapshot: SnapshotDal) => {
                // if the snapshot is a foreign NOGO snapshot or
                // a signature dependency of one (removing the snapshot causes aggStore to be in conflict)
                if (isNogo(snapshot) || hasSignatureConflict(aggStore, snapshot.getPreviousSnapshot()!)) {
                    const snapshotStore = getSnapshotStore(snapshot)
                    mergeStores(aggStore, snapshotStore)
                    nogosAndDependencies.add(snapshot)
                }
            })
            return nogosAndDependencies
        }

        const isUndoablePointer = (pointer: Pointer) => {
            const {type, id} = pointer
            return undoableTypes[type] && !_.get(nonUndoableKeys, [type, id])
        }

        const setInDal = (pointer: Pointer, value: any) => {
            if (isUndoablePointer(pointer)) {
                if (value === undefined) {
                    dal.remove(pointer)
                } else {
                    dal.setIfChanged(pointer, value)
                }
            }
        }

        const buildAndApplyRevert = (desiredSnapshotDal: SnapshotDal, currentSnapshotDal: SnapshotDal): void => {
            const chain = createSnapshotChain(desiredSnapshotDal, currentSnapshotDal, 'buildAndApplyRevert')
            const nogosAndDependencies: Set<SnapshotDal> = markNogosAndDependencies(chain)
            const revertStore: Store = shouldUseFragments ? createFragmentStore() : createStore()
            chain.forEach((snapshot: SnapshotDal) => {
                const snapshotStore = getSnapshotStore(snapshot)
                if (nogosAndDependencies.has(snapshot)) {
                    mergeStores(revertStore, snapshotStore)
                } else if (snapshot.isForeign) {
                    // Check if the snapshot has signature dependencies on any previous snapshots that have been reverted.
                    // Creating the temporary snapshot enables checking for conflicts between snapshot and revertStore,
                    // and for conflicts between snapshot and desiredSnapshotDal (for values not contained in revertStore)
                    if (isConflictingOrDependent(snapshotStore, new SnapshotDal(desiredSnapshotDal, revertStore))) {
                        revertSnapshot(snapshot, desiredSnapshotDal, revertStore)
                    } else {
                        mergeStores(revertStore, snapshotStore)
                    }
                } else {
                    revertSnapshot(snapshot, desiredSnapshotDal, revertStore)
                }
                applyRevertOperation(snapshotStore, (pointer: Pointer) => setInDal(pointer, revertStore.get(pointer)))
            })
        }

        const preAdd = (snapshotLabel: string, params?: object, amendToSnapshotWithId?: string): SnapshotRef => {
            redoStack.clear()
            const shouldAmend = !!amendToSnapshotWithId && lastSnapshotId.toString() === amendToSnapshotWithId
            if (!shouldAmend) {
                undoStack.push(currentSnapshotRef)
            } else {
                params = _.assign({}, currentSnapshotRef.params, params)
            }

            currentSnapshotRef = createSnapshotRef(snapshotLabel, undefined, {params})
            return currentSnapshotRef
        }

        const executeAdd = (newSnapshotRef: SnapshotRef) => {
            newSnapshotRef.index = snapshots.takeSnapshot(UNDO_REDO_TAG)
            eventEmitter.emit(EVENTS.UNDO_REDO.TAKE_SNAPSHOT)
        }

        /**
         * capture a snapshot (current site state) and move the last snapshot to the undo stack.
         * @param {String} [snapshotLabel] - a name describing the snapshot
         * @param {Object} [params] - extra parameters that will be stored with the snapshot in the stack.
         * @param {String} [amendToSnapshotWithId] - id of a snapshot to amend to, if the latest snapshot is equal to this id, the snapshot will be amended, otherwise a new snapshot will be taken
         * Can be restored before performing an undo/redo action by calling getUndo/RedoLastSnapshotParams()
         */
        const add = (snapshotLabel: string, params?: object, amendToSnapshotWithId?: string) => {
            if (!soq) {
                throw new Error('not initialized with soq')
            }
            const newSnapshotRef = preAdd(snapshotLabel, params, amendToSnapshotWithId)
            soq.flushQueueAndExecute(() => {
                executeAdd(newSnapshotRef)
            })
        }

        const isSaveInProgress = () => (extensionAPI as SaveStateApi).saveState.isSaveInProgress()

        /**
         * Returns true if there is at least one snapshot to undo.
         * @returns {boolean}
         */
        const canUndo = () => !isSaveInProgress() && !undoStack.isEmpty()

        /**
         * Returns true if there is at least one snapshot to redo
         * @returns {boolean}
         */
        const canRedo = () => !isSaveInProgress() && !redoStack.isEmpty()

        const executeUndoRedo = (
            isRedo: boolean,
            operation: string,
            event: string,
            targetSnapshotRef: SnapshotRef,
            revertSnapshotRef: SnapshotRef
        ) => {
            try {
                const undoExecInteraction = `undoRedo ${operation} execute`
                logger.interactionStarted(undoExecInteraction)

                const currentSnapshotDal: SnapshotDal = snapshots.getCurrentSnapshot()
                revertSnapshotRef.index = snapshots.tagSnapshot(UNDO_REDO_TAG, currentSnapshotDal)

                const desiredSnapshotDal: SnapshotDal = snapshots.getSnapshotByTagAndIndex(
                    targetSnapshotRef.tag,
                    targetSnapshotRef.index!
                )
                log.info(
                    `execute ${operation}: ${currentSnapshotDal.id.slice(0, 10)} -> ${desiredSnapshotDal.id.slice(
                        0,
                        10
                    )}`
                )
                buildAndApplyRevert(desiredSnapshotDal, currentSnapshotDal)
                logger.interactionEnded(undoExecInteraction)
                eventEmitter.emit(event)
            } catch (error) {
                coreConfig.logger.captureError(error as Error, {
                    tags: {
                        undoRedo: true,
                        isRedo,
                        cedit: concurrentExperimentOpen
                    }
                })
                throw error
            }
        }

        const runUndoRedoSetOperation = (
            setOperationsQueue: SetOperationsQueue,
            isRedo: boolean,
            operation: string,
            event: string,
            targetSnapshotRef: SnapshotRef,
            revertSnapshotRef: SnapshotRef
        ): void => {
            setOperationsQueue.runSetOperation(
                () => {
                    executeUndoRedo(isRedo, operation, event, targetSnapshotRef, revertSnapshotRef)
                },
                [],
                {methodName: operation, noBatching: true}
            )
        }

        const executeUndo = (
            refs: {targetSnapshotRef: SnapshotRef; revertSnapshotRef: SnapshotRef} | undefined
            //injected from getReturnValue = preUndo:
            //, label: string
        ) => {
            if (!refs) {
                return
            }
            executeUndoRedo(false, 'Undo', EVENTS.UNDO_REDO.UNDO, refs.targetSnapshotRef, refs.revertSnapshotRef)
        }

        const executeRedo = (
            refs: {targetSnapshotRef: SnapshotRef; revertSnapshotRef: SnapshotRef} | undefined
            //injected from getReturnValue = preRedo:
            //, label: string
        ) => {
            if (!refs) {
                return
            }
            executeUndoRedo(true, 'Redo', EVENTS.UNDO_REDO.REDO, refs.targetSnapshotRef, refs.revertSnapshotRef)
        }

        const returnValueForUndo = () => {
            return (canUndo() ? undoStack.top()! : currentSnapshotRef).label
        }

        const returnValueForRedo = () => {
            return (canRedo() ? redoStack.top()! : currentSnapshotRef).label
        }

        const prepareSnapshotsForNextOperation = (
            operation: string,
            targetSnapshotRef: SnapshotRef,
            revertStack: Stack<SnapshotRef>
        ) => {
            const revertSnapshotRef: SnapshotRef = {...currentSnapshotRef, index: undefined}
            revertStack.push(revertSnapshotRef)
            currentSnapshotRef = targetSnapshotRef
            log.info(`schedule ${operation}: ${targetSnapshotRef}`)
            return revertSnapshotRef
        }

        const preUndo = () =>
            //injected from getReturnValue
            //, label: string
            {
                if (canUndo()) {
                    const targetSnapshotRef = undoStack.pop()!
                    const revertSnapshotRef = prepareSnapshotsForNextOperation('Undo', targetSnapshotRef, redoStack)
                    return {targetSnapshotRef, revertSnapshotRef}
                }
            }

        const preRedo = () =>
            //injected from getReturnValue
            //, label: string
            {
                if (canRedo()) {
                    const targetSnapshotRef = redoStack.pop()!
                    const revertSnapshotRef = prepareSnapshotsForNextOperation('Redo', targetSnapshotRef, undoStack)
                    return {targetSnapshotRef, revertSnapshotRef}
                }
            }

        /**
         * Undo the actions the were done between the previous and the last call to add()
         * Push the current snapshot to the redo stack and apply the last snapshot in the undo stack
         * @returns {String} the applied snapshot label
         */
        const undo = (): string => {
            if (!soq) {
                throw new Error('not initialized with soq')
            }
            const returnedValue = returnValueForUndo()
            const preUndoResult = preUndo(/*returnedValue*/)
            if (preUndoResult) {
                runUndoRedoSetOperation(
                    soq!,
                    false,
                    'Undo',
                    EVENTS.UNDO_REDO.UNDO,
                    preUndoResult.targetSnapshotRef,
                    preUndoResult.revertSnapshotRef
                )
            }

            return returnedValue
        }

        /**
         * Redo the actions the were most recently rolled back by calling undo
         * Push the current snapshot to the undo stack and apply the last snapshot in the redo stack
         * @returns {String} the applied snapshot label
         */
        const redo = (): string => {
            if (!soq) {
                throw new Error('not initialized with soq')
            }
            const returnedValue = returnValueForRedo()
            const preRedoResult = preRedo(/*returnedValue*/)
            if (preRedoResult) {
                runUndoRedoSetOperation(
                    soq,
                    true,
                    'Redo',
                    EVENTS.UNDO_REDO.REDO,
                    preRedoResult.targetSnapshotRef,
                    preRedoResult.revertSnapshotRef
                )
            }
            return returnedValue
        }

        const preApplyLatestSnapshot = () => {
            return currentSnapshotRef
        }

        const executeApplyLatestSnapshot = (snapshotAtTheTime: SnapshotRef) => {
            const applied = snapshots.getSnapshotByTagAndIndex(snapshotAtTheTime.tag, snapshotAtTheTime.index as number)
            const current = snapshots.getCurrentSnapshot()
            buildAndApplyRevert(applied, current)
            dal.commitTransaction('applyLatestSnapshot')
        }

        /**
         * Redo the last snapshot taken.
         * @return {string}
         */
        const applyLatestSnapshot = () => {
            if (!soq) {
                throw new Error('not initialized with soq')
            }
            const snapshotAtTheTime = preApplyLatestSnapshot()
            soq.flushQueueAndExecute(() => {
                executeApplyLatestSnapshot(snapshotAtTheTime)
            })

            return currentSnapshotRef.label
        }

        /**
         * Clears the undo/redo stack, meaning that after this method is called, no undo/redo actions will be performed until another snapshot is added
         */
        const clear = () => {
            undoStack.clear()
            redoStack.clear()
            takeInitialSnapshotForUndoRedo(extensionAPI as SnapshotExtApi)
        }

        /**
         * Returns the parameters of the last taken snapshot, or null if there aren't any
         * @returns {Object}
         */
        const getUndoLastSnapshotParams = () => _.get(currentSnapshotRef, ['params'], null)

        /**
         * Returns the parameters of the last rolled back snapshot, or null if there aren't any
         * @returns {Object}
         */
        const getRedoLastSnapshotParams = () => _.get(redoStack.top(), ['params'], null)
        /**
         * Returns the label of the last snapshot taken, or an empty string if it wasn't set
         * @returns {String}
         */
        const getUndoLastSnapshotLabel = () => _.get(currentSnapshotRef, ['label'], '')
        /**
         * Returns the label of the last rolled back snapshot, or an empty string if it wasn't set
         * @returns {String}
         */
        const getRedoLastSnapshotLabel = () => _.get(redoStack.top(), ['label'], '')
        /**
         * Returns the id of the last snapshot taken, or an empty string if it wasn't set
         * @returns {String}
         */
        const getUndoLastSnapshotId = () => _.get(currentSnapshotRef, ['id'], '').toString()
        /**
         * Returns the id of the last rolled back snapshot, or an empty string if it wasn't set
         * @returns {String}
         */
        const getRedoLastSnapshotId = () => _.get(redoStack.top(), ['id'], '').toString()

        const getCurrentSnapshotId = (): string => snapshots.getCurrentSnapshot().id

        const hasChangesSinceSnapshot = (snapshotId: string): boolean => {
            const snapshot = snapshots.getSnapshotById(snapshotId)
            if (!snapshot) {
                return false
            }
            const changes = snapshots.getChangesFromSnapshot?.(snapshot)
            if (!changes) {
                return false
            }
            return Object.keys(changes).some(type => undoableTypes[type] && !_.isEmpty(changes[type]))
        }

        const initWithSOQ = (setOperationsQueue: SetOperationsQueue) => {
            soq = setOperationsQueue
        }

        return {
            undoRedo: {
                undo,
                redo,
                add,
                applyLatestSnapshot,
                clear,
                getUndoLastSnapshotParams,
                getRedoLastSnapshotParams,
                getUndoLastSnapshotLabel,
                getRedoLastSnapshotLabel,
                getUndoLastSnapshotId,
                getRedoLastSnapshotId,
                canUndo,
                canRedo,
                getCurrentSnapshotId,
                hasChangesSinceSnapshot,
                executeUndo,
                returnValueForUndo,
                preUndo,
                executeRedo,
                returnValueForRedo,
                preRedo,
                preAdd,
                executeAdd,
                preApplyLatestSnapshot,
                executeApplyLatestSnapshot
            },
            initWithSOQ,
            takeInitialSnapshotForUndoRedo: () => takeInitialSnapshotForUndoRedo(extensionAPI as SnapshotExtApi),

            // Test Apis
            _buildAndApplyRevert: buildAndApplyRevert,
            _markNogosAndDependencies: markNogosAndDependencies
        }
    }

    const createPublicAPI = (args: CreateExtArgs): UndoRedoPublicApi => {
        const {undoRedo} = args.extensionAPI as UndoRedoExtApi
        return {
            undoRedo: {
                undo(): string {
                    return undoRedo.undo()
                },
                redo(): string {
                    return undoRedo.redo()
                },
                add(snapshotLabel: string, params?: object, amendToSnapshotWithId?: string): void {
                    undoRedo.add(snapshotLabel, params, amendToSnapshotWithId)
                },
                applyLatestSnapshot(): string {
                    return undoRedo.applyLatestSnapshot()
                },
                clear(): void {
                    undoRedo.clear()
                },
                getUndoLastSnapshotParams(): any {
                    return undoRedo.getUndoLastSnapshotParams()
                },
                getRedoLastSnapshotParams(): any {
                    return undoRedo.getRedoLastSnapshotParams()
                },
                getUndoLastSnapshotLabel(): string {
                    return undoRedo.getUndoLastSnapshotLabel()
                },
                getRedoLastSnapshotLabel(): string {
                    return undoRedo.getRedoLastSnapshotLabel()
                },
                getUndoLastSnapshotId(): string {
                    return undoRedo.getUndoLastSnapshotId()
                },
                getRedoLastSnapshotId(): string {
                    return undoRedo.getRedoLastSnapshotId()
                },
                canUndo(): boolean {
                    return undoRedo.canUndo()
                },
                canRedo(): boolean {
                    return undoRedo.canRedo()
                },
                getCurrentSnapshotId(): string {
                    return undoRedo.getCurrentSnapshotId()
                },
                hasChangesSinceSnapshot(snapshotId: string): boolean {
                    return undoRedo.hasChangesSinceSnapshot(snapshotId)
                },
                executeUndo(
                    refs: {targetSnapshotRef: SnapshotRef; revertSnapshotRef: SnapshotRef} | undefined,
                    label: string
                ): void {
                    undoRedo.executeUndo(refs, label)
                },
                returnValueForUndo(): string {
                    return undoRedo.returnValueForUndo()
                },
                preUndo(label: string):
                    | {
                          targetSnapshotRef: SnapshotRef
                          revertSnapshotRef: SnapshotRef
                      }
                    | undefined {
                    return undoRedo.preUndo(label)
                },
                executeRedo(
                    refs: {targetSnapshotRef: SnapshotRef; revertSnapshotRef: SnapshotRef} | undefined,
                    label: string
                ): void {
                    undoRedo.executeRedo(refs, label)
                },
                returnValueForRedo(): string {
                    return undoRedo.returnValueForRedo()
                },
                preRedo(label: string):
                    | {
                          targetSnapshotRef: SnapshotRef
                          revertSnapshotRef: SnapshotRef
                      }
                    | undefined {
                    return undoRedo.preRedo(label)
                },
                preAdd(snapshotLabel: string, params?: object, amendToSnapshotWithId?: string): SnapshotRef {
                    return undoRedo.preAdd(snapshotLabel, params, amendToSnapshotWithId)
                },
                executeAdd(newSnapshotRef: SnapshotRef): void {
                    undoRedo.executeAdd(newSnapshotRef)
                },
                preApplyLatestSnapshot(): SnapshotRef {
                    return undoRedo.preApplyLatestSnapshot()
                },
                executeApplyLatestSnapshot(snapshotAtTheTime: SnapshotRef): void {
                    undoRedo.executeApplyLatestSnapshot(snapshotAtTheTime)
                }
            }
        }
    }

    const initialize = async ({eventEmitter, EVENTS: events, extensionAPI}: InitializeExtArgs) => {
        eventEmitter.on(events.PUBLIC_APIS.IS_READY, () => {
            takeInitialSnapshotForUndoRedo(extensionAPI as SnapshotExtApi)
        })
    }

    return {
        name: 'undoRedo',
        createPublicAPI,
        createExtensionAPI,
        EVENTS,
        initialize
    }
}

export interface UndoRedoApi extends ExtensionAPI {
    undo(): string
    redo(): string
    add(snapshotLabel: string, params?: object, amendToSnapshotWithId?: string): void
    applyLatestSnapshot(): string
    clear(): void
    getUndoLastSnapshotParams(): any
    getRedoLastSnapshotParams(): any
    getUndoLastSnapshotLabel(): string
    getRedoLastSnapshotLabel(): string
    getUndoLastSnapshotId(): string
    getRedoLastSnapshotId(): string
    canUndo(): boolean
    canRedo(): boolean
    getCurrentSnapshotId(): string
    hasChangesSinceSnapshot(snapshotId: string): boolean
    executeUndo(refs: {targetSnapshotRef: SnapshotRef; revertSnapshotRef: SnapshotRef} | undefined, label: string): void
    returnValueForUndo(): string
    preUndo(label: string):
        | {
              targetSnapshotRef: SnapshotRef
              revertSnapshotRef: SnapshotRef
          }
        | undefined
    executeRedo(refs: {targetSnapshotRef: SnapshotRef; revertSnapshotRef: SnapshotRef} | undefined, label: string): void
    returnValueForRedo(): string
    preRedo(label: string):
        | {
              targetSnapshotRef: SnapshotRef
              revertSnapshotRef: SnapshotRef
          }
        | undefined
    preAdd(snapshotLabel: string, params?: object, amendToSnapshotWithId?: string): SnapshotRef
    executeAdd(newSnapshotRef: SnapshotRef): void
    preApplyLatestSnapshot(): SnapshotRef
    executeApplyLatestSnapshot(snapshotAtTheTime: SnapshotRef): void
}

export type UndoRedoExtApi = ExtensionAPI & {
    undoRedo: UndoRedoApi
    initWithSOQ(setOperationsQueue: SetOperationsQueue): void
    takeInitialSnapshotForUndoRedo(): void

    // Test Apis
    _buildAndApplyRevert(desiredSnapshot: SnapshotDal, currentSnapshot: SnapshotDal): void
    _markNogosAndDependencies(chain: SnapshotDal[]): Set<SnapshotDal>
}

interface UndoRedoPublicApi extends ExtensionAPI {
    undoRedo: UndoRedoApi
}

export {createExtension, EVENTS}
